// Iter BB — beta-tester invite management (numbered, assignable, filterable).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Check, X, Pencil, Eye, Send } from "lucide-react";
import { api } from "../lib/api";

const CATEGORIES = [
  { key: "solo", label: "Solo Practitioner" },
  { key: "multi", label: "Multi-Therapist Practice" },
  { key: "new_grad", label: "New Graduate" },
  { key: "educator", label: "Educator" },
  { key: "other", label: "Other" },
];
const catLabel = (k) => (CATEGORIES.find((c) => c.key === k) || {}).label || null;

const ENTITLEMENTS = [
  { key: "free", label: "Free" },
  { key: "founding", label: "Founding Member" },
  { key: "professional", label: "Professional" },
  { key: "clinic", label: "Clinic Pro" },
  { key: "lifetime", label: "Lifetime / Full Beta Access" },
];
const entLabel = (k) => (ENTITLEMENTS.find((e) => e.key === k) || {}).label || "Free";

// Display text + tone for an entitlement summary from the backend.
function entBadge(e) {
  if (!e) return null;
  if (e.mismatch) return { text: `⚠ ${entLabel(e.entitlement)} but limited`, tone: "bg-terracotta-500/15 text-terracotta-700" };
  const flag = e.odd_tier ? " ⚑" : "";
  if (e.entitlement === "lifetime") return { text: `∞ Lifetime · Full Access${flag}`, tone: "bg-sage-500/15 text-sage-700" };
  if (e.unlimited) {
    const label = e.entitlement && e.entitlement !== "free" ? entLabel(e.entitlement) : (e.is_admin ? "Admin" : e.tier_label);
    return { text: `∞ ${label} · Unlimited${flag}`, tone: "bg-sage-500/15 text-sage-700" };
  }
  return { text: `${entLabel(e.entitlement)} · limited`, tone: "bg-paper-100 text-ink-600" };
}

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  sent: "bg-sky-100 text-sky-700",
  accepted: "bg-sage-500/10 text-sage-700",
  expired: "bg-paper-100 text-ink-500",
  revoked: "bg-terracotta-500/10 text-terracotta-700",
};
const STATUSES = ["pending", "sent", "accepted", "revoked", "expired"];

const num = (n) => `#${String(n || 0).padStart(3, "0")}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

const inputCls = "w-full min-h-[42px] px-3.5 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm text-ink-900";

function AssignmentForm({ form, setForm }) {
  return (
    <div className="space-y-2.5">
      <input data-testid="invite-form-name" placeholder="Full name (optional)" value={form.assigned_name} onChange={(e) => setForm({ ...form, assigned_name: e.target.value })} className={inputCls} />
      <input data-testid="invite-form-email" type="email" placeholder="Email address (optional)" value={form.assigned_email} onChange={(e) => setForm({ ...form, assigned_email: e.target.value })} className={inputCls} />
      <select data-testid="invite-form-category" value={form.tester_category} onChange={(e) => setForm({ ...form, tester_category: e.target.value })} className={inputCls}>
        <option value="">Tester category (optional)</option>
        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <div>
        <label className="block text-xs font-medium text-ink-600 mb-1">Access / Entitlement</label>
        <select data-testid="invite-form-entitlement" value={form.entitlement} onChange={(e) => setForm({ ...form, entitlement: e.target.value })} className={inputCls}>
          {ENTITLEMENTS.map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
        </select>
        <p className="text-[11px] text-ink-500 mt-1">Beta testers default to full access. Category is descriptive only and does not affect access.</p>
      </div>
      <textarea data-testid="invite-form-notes" rows={2} placeholder="Notes — anything you want to remember (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} py-2.5 resize-none`} />
    </div>
  );
}

function Dialog({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[70] bg-ink-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={testid}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-paper-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="font-heading text-lg text-ink-900">{title}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="w-8 h-8 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500 shrink-0">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY_FORM = { assigned_name: "", assigned_email: "", tester_category: "", entitlement: "lifetime", notes: "" };

export const InviteManager = () => {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [details, setDetails] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [bulkActivity, setBulkActivity] = useState({});
  const [sortBy, setSortBy] = useState("newest");
  const [revoking, setRevoking] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
  const loadActivity = () => api.get("/invites/activity").then((r) => setBulkActivity(r.data || {})).catch(() => {});
  useEffect(() => { load(); loadActivity(); }, []);

  useEffect(() => {
    setActivity(null);
    if (details && details.status === "accepted") {
      setActivityLoading(true);
      api.get(`/invites/${details.id}/activity`)
        .then((r) => setActivity(r.data))
        .catch(() => setActivity({ available: false, reason: "Could not load activity." }))
        .finally(() => setActivityLoading(false));
    }
  }, [details]);

  const filtered = useMemo(() => {
    let list = invites;
    if (statusFilter) list = list.filter((i) => i.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        (i.assigned_name || "").toLowerCase().includes(q) ||
        (i.assigned_email || "").toLowerCase().includes(q) ||
        (i.used_by_email || "").toLowerCase().includes(q) ||
        num(i.invite_number).includes(q) ||
        String(i.invite_number).includes(q)
      );
    }
    if (sortBy === "active") {
      const score = (i) => {
        const a = bulkActivity[i.id];
        if (!a || !a.available) return { t: "", s: -1 };
        return { t: a.last_active_at || "", s: a.sessions_completed || 0 };
      };
      list = [...list].sort((x, y) => {
        const sx = score(x), sy = score(y);
        return sy.t.localeCompare(sx.t) || (sy.s - sx.s) || (y.invite_number - x.invite_number);
      });
    }
    return list;
  }, [invites, search, statusFilter, sortBy, bulkActivity]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/invites", {
        assigned_name: form.assigned_name || null,
        assigned_email: form.assigned_email || null,
        tester_category: form.tester_category || null,
        entitlement: form.entitlement || "lifetime",
        notes: form.notes || null,
      });
      toast.success("Invite created — copy the link and send it");
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invite");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/invites/${editing.id}`, {
        assigned_name: form.assigned_name,
        assigned_email: form.assigned_email,
        tester_category: form.tester_category,
        entitlement: form.entitlement,
        notes: form.notes,
      });
      toast.success(`Invite ${num(editing.invite_number)} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update invite");
    } finally { setBusy(false); }
  };

  const copy = async (inv) => {
    navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`);
    setCopiedId(inv.id);
    setTimeout(() => setCopiedId(null), 1500);
    if (inv.status === "pending") {
      try { await api.post(`/invites/${inv.id}/mark-sent`); load(); } catch { /* non-fatal */ }
    }
  };

  const confirmRevoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/invites/${revoking.id}`);
      toast.success(`Invite ${num(revoking.invite_number)} revoked`);
      setRevoking(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke");
    } finally { setBusy(false); }
  };

  const emailInvite = async (inv) => {
    setBusy(true);
    try {
      await api.post(`/invites/${inv.id}/send-email`, { origin: window.location.origin });
      toast.success(`Invite ${num(inv.invite_number)} emailed to ${inv.assigned_email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setBusy(false); }
  };

  const openEdit = (inv) => {
    setForm({
      assigned_name: inv.assigned_name || "",
      assigned_email: inv.assigned_email || "",
      tester_category: inv.tester_category || "",
      entitlement: inv.entitlement || "lifetime",
      notes: inv.notes || "",
    });
    setEditing(inv);
  };

  const changeEntitlement = async (inviteId, code) => {
    try {
      const r = await api.post(`/invites/${inviteId}/entitlement`, { entitlement: code });
      setBulkActivity((prev) => ({
        ...prev,
        [inviteId]: { ...(prev[inviteId] || {}), entitlement: r.data.entitlement },
      }));
      toast.success("Entitlement updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update entitlement");
    }
  };

  const chip = (active) => `min-h-[32px] px-3 rounded-full text-xs font-medium border transition-colors ${active ? "bg-ink-900 text-white border-ink-900" : "bg-white border-paper-200 text-ink-700 hover:bg-paper-100"}`;

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="invite-testers-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl font-medium text-ink-900">Invite testers</h2>
          <p className="text-sm text-ink-500 mt-1">Single-use links, valid 7 days. Assign each invite to a tester so you always know which link belongs to whom.</p>
        </div>
        <button data-testid="generate-invite-btn" onClick={() => { setForm(EMPTY_FORM); setCreateOpen(true); }} className="min-h-[40px] px-4 rounded-full bg-ink-900 hover:bg-ink-900/90 text-white text-sm font-medium inline-flex items-center gap-2 shrink-0">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New invite
        </button>
      </div>

      {invites.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            data-testid="invite-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or #number…"
            className="flex-1 min-w-[180px] min-h-[36px] px-3.5 rounded-full bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 outline-none text-xs text-ink-900"
          />
          <button className={chip(!statusFilter)} onClick={() => setStatusFilter("")} data-testid="invite-filter-all">All</button>
          {STATUSES.map((s) => (
            <button key={s} className={chip(statusFilter === s)} onClick={() => setStatusFilter(s)} data-testid={`invite-filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.1em] text-ink-500 font-semibold">Sort</span>
            <button
              data-testid="invite-sort-newest"
              onClick={() => setSortBy("newest")}
              className={chip(sortBy === "newest")}
            >
              Newest
            </button>
            <button
              data-testid="invite-sort-active"
              onClick={() => setSortBy("active")}
              className={chip(sortBy === "active")}
            >
              Most active
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-xs text-ink-500 text-center py-6" data-testid="invite-empty">
          {invites.length === 0 ? "No invites yet — create your first one." : "No invites match this filter."}
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="invite-list">
          {filtered.map((inv) => (
            <li key={inv.id} className="bg-paper-50 border border-paper-200 rounded-2xl px-4 py-3" data-testid={`invite-card-${inv.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-mono text-sm font-semibold text-ink-900" data-testid={`invite-number-${inv.id}`}>{num(inv.invite_number)}</span>
                <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-[0.1em] text-[9px] ${STATUS_CLS[inv.status]}`} data-testid={`invite-status-${inv.id}`}>{inv.status}</span>
                {inv.tester_category && <span className="px-2 py-0.5 rounded-full bg-white border border-paper-200 text-[10px] text-ink-700">{catLabel(inv.tester_category)}</span>}
                <span className="ml-auto text-[10px] text-ink-500">Created {dt(inv.created_at)}</span>
              </div>
              <div className="text-xs text-ink-900 font-medium" data-testid={`invite-assignee-${inv.id}`}>
                {inv.assigned_name || <span className="text-ink-500 font-normal italic">Unassigned</span>}
                {inv.assigned_email && <span className="text-ink-500 font-normal"> · {inv.assigned_email}</span>}
              </div>
              <div className="text-[10px] text-ink-500 mt-0.5">
                {inv.status === "accepted"
                  ? <>Accepted {dt(inv.used_at)} by {inv.used_by_email}</>
                  : inv.status === "revoked"
                    ? <>Revoked {dt(inv.revoked_at)}</>
                    : <>Expires {dt(inv.expires_at)}{inv.sent_at ? ` · Sent ${dt(inv.sent_at)}` : ""}</>}
              </div>
              {inv.status === "accepted" && bulkActivity[inv.id]?.available && (
                <div className="text-[10px] text-ink-700 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`invite-activity-mini-${inv.id}`}>
                  {(() => {
                    const b = entBadge(bulkActivity[inv.id].entitlement);
                    if (!b) return null;
                    return <span className={`px-1.5 py-0.5 rounded-full font-semibold ${b.tone}`} data-testid={`invite-entitlement-${inv.id}`}>{b.text}</span>;
                  })()}
                  <span className="text-ink-400">·</span>
                  <span className="font-semibold">{bulkActivity[inv.id].clients_created} clients</span>
                  <span className="text-ink-400">·</span>
                  <span>{bulkActivity[inv.id].sessions_started} sessions ({bulkActivity[inv.id].sessions_completed} done)</span>
                  {bulkActivity[inv.id].last_active_at
                    ? <><span className="text-ink-400">·</span><span>active {dt(bulkActivity[inv.id].last_active_at)}</span></>
                    : <><span className="text-ink-400">·</span><span className="text-ink-400 italic">no activity yet</span></>}
                </div>
              )}
              {inv.notes && <div className="text-[11px] text-ink-700 mt-1.5 bg-white border border-paper-200 rounded-xl px-2.5 py-1.5 line-clamp-2">{inv.notes}</div>}
              <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] font-medium">
                {(inv.status === "pending" || inv.status === "sent") && (
                  <>
                    <button data-testid={`copy-invite-${inv.id}`} onClick={() => copy(inv)} className="text-sage-700 hover:text-sage-600 inline-flex items-center gap-1">
                      {copiedId === inv.id ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                    </button>
                    <button data-testid={`edit-invite-${inv.id}`} onClick={() => openEdit(inv)} className="text-ink-700 hover:text-ink-900 inline-flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      data-testid={`email-invite-${inv.id}`}
                      onClick={() => emailInvite(inv)}
                      disabled={busy || !inv.assigned_email}
                      title={inv.assigned_email ? `Email the link to ${inv.assigned_email}` : "Assign an email via Edit first"}
                      className={`inline-flex items-center gap-1 ${inv.assigned_email ? "text-sage-700 hover:text-sage-600" : "text-ink-400 cursor-not-allowed"}`}
                    >
                      <Send className="w-3 h-3" /> {inv.last_emailed_at ? "Resend email" : "Email invite"}
                    </button>
                    <button data-testid={`revoke-invite-${inv.id}`} onClick={() => setRevoking(inv)} className="text-ink-500 hover:text-terracotta-600 underline underline-offset-2">Revoke</button>
                  </>
                )}
                <button data-testid={`details-invite-${inv.id}`} onClick={() => setDetails(inv)} className="text-ink-500 hover:text-ink-900 inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Details
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <Dialog title="New tester invite" onClose={() => setCreateOpen(false)} testid="invite-create-dialog">
          <p className="text-xs text-ink-500 mb-3">Assign this invite so it's easy to identify later. All fields optional — the tester still sets their own credentials.</p>
          <AssignmentForm form={form} setForm={setForm} />
          <button data-testid="invite-create-submit" onClick={create} disabled={busy} className="w-full mt-4 min-h-[46px] rounded-full bg-ink-900 hover:bg-ink-900/90 disabled:opacity-50 text-white text-sm font-medium">
            {busy ? "Creating…" : "Generate invite"}
          </button>
        </Dialog>
      )}

      {editing && (
        <Dialog title={`Edit invite ${num(editing.invite_number)}`} onClose={() => setEditing(null)} testid="invite-edit-dialog">
          <AssignmentForm form={form} setForm={setForm} />
          <button data-testid="invite-edit-submit" onClick={saveEdit} disabled={busy} className="w-full mt-4 min-h-[46px] rounded-full bg-ink-900 hover:bg-ink-900/90 disabled:opacity-50 text-white text-sm font-medium">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </Dialog>
      )}

      {details && (
        <Dialog title={`Invite ${num(details.invite_number)}`} onClose={() => setDetails(null)} testid="invite-details-dialog">
          <dl className="space-y-2 text-xs">
            {[
              ["Status", details.status],
              ["Assigned to", details.assigned_name || "Unassigned"],
              ["Email", details.assigned_email || "—"],
              ["Category", catLabel(details.tester_category) || "—"],
              ["Access", entLabel(details.entitlement)],
              ["Created", dt(details.created_at)],
              ["Expires", dt(details.expires_at)],
              ["Sent", dt(details.sent_at)],
              ["Accepted", details.used_at ? `${dt(details.used_at)} by ${details.used_by_email}` : "—"],
              ["Revoked", dt(details.revoked_at)],
              ["Notes", details.notes || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-3">
                <dt className="w-24 shrink-0 text-ink-500">{k}</dt>
                <dd className="text-ink-900 capitalize-first">{v}</dd>
              </div>
            ))}
            {(details.status === "pending" || details.status === "sent") && (
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-ink-500">Link</dt>
                <dd className="text-ink-900 break-all font-mono text-[10px]">{`${window.location.origin}/invite/${details.token}`}</dd>
              </div>
            )}
          </dl>
          {details.status === "accepted" && (
            <div className="mt-5 pt-4 border-t border-paper-200" data-testid="invite-activity-section">
              {(() => {
                const e = bulkActivity[details.id]?.entitlement;
                if (!e) return null;
                const b = entBadge(e);
                return (
                  <div className="mb-4" data-testid="invite-entitlement-detail">
                    <div className="label-overline mb-1.5">Entitlement</div>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${b.tone}`} data-testid="invite-entitlement-detail-badge">{b.text}</span>
                    {e.early_adopter_granted && (
                      <div className="text-[11px] text-ink-500 mt-1.5">
                        Grant: {e.grant_type || "—"} · stored tier {e.ea_stored_tier || "—"}
                        {e.expires_at ? ` · expires ${dt(e.expires_at)}` : " · permanent"}
                        {e.odd_tier && <span className="text-amber-700"> · ⚑ non-standard tier, review</span>}
                      </div>
                    )}
                    <div className="mt-2.5">
                      <label className="block text-[11px] font-medium text-ink-600 mb-1">Change entitlement</label>
                      <select
                        data-testid="invite-entitlement-select"
                        value={e.entitlement || "free"}
                        onChange={(ev) => changeEntitlement(details.id, ev.target.value)}
                        className={inputCls}
                      >
                        {ENTITLEMENTS.map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
                      </select>
                      <p className="text-[11px] text-ink-500 mt-1">Updates the tester's real account access immediately.</p>
                    </div>
                  </div>
                );
              })()}
              <div className="font-heading text-sm text-ink-900 mb-2.5">Tester activity</div>
              {activityLoading ? (
                <div className="text-xs text-ink-500" data-testid="invite-activity-loading">Loading activity…</div>
              ) : !activity ? null : !activity.available ? (
                <div className="text-xs text-ink-500" data-testid="invite-activity-unavailable">
                  Not available — {activity.reason || "no data."}
                </div>
              ) : (
                <dl className="space-y-2 text-xs" data-testid="invite-activity-data">
                  {[
                    ["Last active", activity.last_active_at ? dt(activity.last_active_at) : "Not available"],
                    ["Last login", activity.last_login_at ? dt(activity.last_login_at) : "Not available"],
                    ["Clients created", String(activity.clients_created ?? 0)],
                    ["Sessions started", String(activity.sessions_started ?? 0)],
                    ["Sessions completed", String(activity.sessions_completed ?? 0)],
                    ["Most recent session", activity.most_recent_session_at ? dt(activity.most_recent_session_at) : "None yet"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <dt className="w-32 shrink-0 text-ink-500">{k}</dt>
                      <dd className="text-ink-900">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
        </Dialog>
      )}

      {revoking && (
        <Dialog title="Revoke invite?" onClose={() => setRevoking(null)} testid="invite-revoke-dialog">
          <p className="text-sm text-ink-700 mb-5" data-testid="invite-revoke-message">
            Are you sure you want to revoke <strong>Invite {num(revoking.invite_number)}</strong>
            {revoking.assigned_name ? <> assigned to <strong>{revoking.assigned_name}</strong></> : " (unassigned)"}?
            This action cannot be undone.
          </p>
          <div className="flex gap-2">
            <button data-testid="invite-revoke-cancel" onClick={() => setRevoking(null)} className="flex-1 min-h-[44px] rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 text-sm">Keep invite</button>
            <button data-testid="invite-revoke-confirm" onClick={confirmRevoke} disabled={busy} className="flex-1 min-h-[44px] rounded-full bg-terracotta-600 hover:bg-terracotta-600/90 disabled:opacity-50 text-white text-sm font-medium">
              {busy ? "Revoking…" : "Revoke"}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};
